import React, {useRef} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'

import {Button} from '@vio/atlas/Button'
import {Grid} from '@vio/atlas/helpers/Grid'
import {
  InputButton,
  InputButtonStyledContainer
} from '@vio/atlas/helpers/InputButton'
import {Icon} from '@vio/atlas/Icon'
import {truncateSingleLine} from '@vio/atlas/src/utils/truncate'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {TrackEventPayload} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {DateValidationReason} from '@vio/core/src/searchParams/dateValidationReason'
import {dateStringToMiddayDate} from '@vio/core/src/utils/date'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {getDateConfirmationTooltipShown} from '../cookies/utils'
import {datePickerMessages} from '../DatePicker/helpers/datePickerMessages'
import {guestPickerMessages} from '../GuestPicker/guestPickerMessages'
import {SearchDateTooltipWrapper} from '../SearchDateTooltipWrapper'
import {
  DateTooltipAnchor,
  DateTooltipWrapper
} from '../SearchDateTooltipWrapper/SearchDateTooltipWrapper'
import {SearchFormDestinationInput} from './SearchFormDestinationInput'
import {
  DIVIDER_WIDTH,
  formButtonFocusBorderStyles,
  formButtonFocusIconStyles,
  IconWrapper
} from './styles'

interface BaseProps {
  /** Check in date */
  checkIn: string
  /** Check out date */
  checkOut: string
  /** Rooms configuration */
  numberOfAdults: number
  /** Rooms configuration */
  numberOfChildren: number
  /** Rooms configuration */
  numberOfRooms: number
  /** Callback for check-in date picker click */
  onCheckInDatePickerClick: () => void
  /** Callback for check-out date picker click */
  onCheckOutDatePickerClick: () => void
  /** Callback for Guest picker click */
  onGuestPickerClick: () => void
  /** Callback for Submit click */
  onSubmit: () => void
  /** Whether the check in picker popover is currently open */
  isCheckInPickerOpen: boolean
  /** Whether the check out picker popover is currently open */
  isCheckOutPickerOpen: boolean
  /** Whether the guest picker popover is currently open */
  isGuestPickerOpen: boolean
  /** A ref to the guest picker container */
  guestPickerRef: React.RefObject<HTMLDivElement>
  /** Whether the rooms count and label is shown */
  isRoomsLabelShown?: boolean
  /** Wether should show the date confirmation tooltip */
  isDateConfirmationTooltipShown?: boolean
  /** The date validation reason for scenario-specific tooltip messages */
  dateValidationReason?: DateValidationReason | null
  /** Track event function. Temporary solution to track events without crashing until redux analytics middleware is added.*/
  trackEvent: (payload: TrackEventPayload) => void
  /** Whether the search form is on the home page */
  isHomePage?: boolean
}

interface PropsWithDestination extends BaseProps {
  /** Destination name displayed in the destination button */
  destinationButtonDisplayValue: string
  /** Destination name displayed in the destination input */
  destinationInputDisplayValue: string
  /** Callback for Destination picker click */
  onDestinationPickerClick: (
    event: React.SyntheticEvent<Element, Event>
  ) => void
  /** Whether the destination picker popover is currently open */
  isDestinationPickerOpen: boolean
  /** A ref to the destination input */
  destinationInputRef?: React.RefObject<HTMLDivElement>
  /** Callback for suggestions requested */
  onSuggestionsRequested: (
    value: string,
    shouldLoadUserSearchHistory: boolean
  ) => void
  /** A flag to see if the destination input has error */
  destinationHasError?: boolean
}

interface PropsWithoutDestination extends BaseProps {
  destinationButtonDisplayValue?: never
  destinationInputDisplayValue?: never
  onDestinationPickerClick?: never
  destinationInputRef?: never
  isDestinationPickerOpen?: never
  onSuggestionsRequested?: never
  destinationHasError?: never
}

type Props = PropsWithDestination | PropsWithoutDestination

const FormContainer = styled.div`
  width: 100%;
  display: flex;
`

const DateWrapper = styled(Grid)`
  flex: 1 1 124px !important;
  position: relative;
`
const SearchFormGrid = styled(Grid)`
  align-self: flex-start;
  border: solid 1px ${cssTheme.colors.border.neutral.secondary};
  border-radius: ${cssTheme.layout.radius.rounded};
  padding: 3px;
  min-width: 0;
  ${linariaMq.desktopMd} {
    width: 800px;
  }

  ${DateTooltipAnchor} {
    width: 100px;
    left: 8px;
  }
  ${DateTooltipWrapper} {
    display: flex;
    flex-wrap: nowrap;
    position: relative;
    outline: 2px solid ${cssTheme.colors.input.focus.border};
    transition: outline 0.2s ease;
    gap: 0;
    border-radius: ${cssTheme.layout.radius.sm};

    ${linariaMq.desktopXs} {
      flex: 1 1 249px;
    }

    ${linariaMq.desktopSm} {
      flex: 1 1 283px;
    }

    ${linariaMq.desktopMd} {
      flex: 1 1 258px;
    }

    ${DateWrapper} {
      flex: 1 1 124px !important;
      position: relative;

      ${IconWrapper} {
        color: ${cssTheme.colors.content.brand.primary};
      }
    }
  }
`

const DestinationWrapper = styled(Grid)`
  flex: 4 1 190px !important;
  min-width: 0;
  position: relative;
`

const GuestWrapper = styled.div`
  max-width: 190px;
  flex: 1 1 182px !important;
  position: relative;
`

const Divider = styled.div`
  width: 0;
  align-self: center;
  border-right: 1px solid ${cssTheme.colors.border.neutral.secondary};
  height: ${cssTheme.layout.spacing.s600};
`

const FormButton = styled(InputButton)`
  &.--is-active {
    position: absolute;
    top: 0;
    left: -${DIVIDER_WIDTH}px;
    width: calc(100% + ${DIVIDER_WIDTH * 2}px);
    padding: 0 ${DIVIDER_WIDTH * 2}px !important;
    background: white;
    ${InputButtonStyledContainer} {
      background: initial;
      ${formButtonFocusBorderStyles}
    }
    ${IconWrapper} {
      padding-left: ${DIVIDER_WIDTH}px;
      ${formButtonFocusIconStyles}
    }
    &.--is-first-input {
      border-top-left-radius: ${cssTheme.layout.radius.rounded};
      border-bottom-left-radius: ${cssTheme.layout.radius.rounded};
    }
  }
  &.--has-error {
    padding: 0 ${DIVIDER_WIDTH}px;

    ${InputButtonStyledContainer} {
      border-color: ${cssTheme.colors.border.danger.primary};
    }
    ${IconWrapper} {
      color: ${cssTheme.colors.content.danger.secondary};
    }

    :hover {
      ${InputButtonStyledContainer} {
        border-color: ${cssTheme.colors.border.danger.primary};
      }
    }
    :focus {
      border-color: ${cssTheme.colors.border.danger.primary};
    }
  }
  padding: 0 ${DIVIDER_WIDTH}px;

  ${InputButtonStyledContainer} {
    border: 1px solid transparent;
  }
  ${IconWrapper} {
    color: ${cssTheme.colors.content.neutral.secondary};
  }

  &:focus,
  &:hover {
    position: absolute;
    top: 0;
    left: -${DIVIDER_WIDTH}px;
    width: calc(100% + ${DIVIDER_WIDTH * 2}px);
    padding: 0 ${DIVIDER_WIDTH * 2}px !important;
    background: white;

    ${InputButtonStyledContainer} {
      background: initial;
    }
    ${IconWrapper} {
      padding-left: ${DIVIDER_WIDTH}px;
    }

    &.--is-first-input {
      border-top-left-radius: ${cssTheme.layout.radius.rounded};
      border-bottom-left-radius: ${cssTheme.layout.radius.rounded};
    }
  }
  &:hover {
    ${InputButtonStyledContainer} {
      border-color: ${cssTheme.colors.border.neutral.primary};
    }
  }
  &:focus {
    ${InputButtonStyledContainer} {
      ${formButtonFocusBorderStyles}
    }
    ${IconWrapper} {
      ${formButtonFocusIconStyles}
    }
  }
`

const SearchButtonWrapper = styled.div`
  margin-left: 1px;
`

const ButtonContent = styled.div`
  ${truncateSingleLine()}
`

export const destinationPickerMessages = defineMessages({
  destinationPlaceholder: {
    id: 'whereAreYouGoing',
    defaultMessage: 'Where are you going?'
  }
})

export const SearchFormHorizontal = React.memo(function SearchFormHorizontal({
  checkIn,
  checkOut,
  destinationButtonDisplayValue,
  destinationInputDisplayValue,
  numberOfAdults,
  numberOfChildren,
  numberOfRooms,
  onDestinationPickerClick,
  onCheckInDatePickerClick,
  onCheckOutDatePickerClick,
  onGuestPickerClick,
  onSubmit,
  isDestinationPickerOpen,
  isCheckInPickerOpen,
  isCheckOutPickerOpen,
  isGuestPickerOpen,
  guestPickerRef,
  onSuggestionsRequested,
  destinationInputRef,
  destinationHasError = false,
  isRoomsLabelShown = true,
  isDateConfirmationTooltipShown = false,
  dateValidationReason,
  trackEvent
}: Props) {
  const {formatDate, formatMessage} = useIntl()

  const showDestination = Boolean(onDestinationPickerClick)
  const numberOfGuests = numberOfAdults + numberOfChildren

  const guestMessage = formatMessage(guestPickerMessages.guest, {
    count: numberOfGuests
  })

  const roomsMessage = formatMessage(guestPickerMessages.room, {
    count: numberOfRooms
  })

  const checkInDate =
    checkIn &&
    formatDate(dateStringToMiddayDate(checkIn), {
      day: '2-digit',
      month: 'short'
    })

  const checkOutDate =
    checkOut &&
    formatDate(dateStringToMiddayDate(checkOut), {
      day: '2-digit',
      month: 'short'
    })

  // Check cookie only once when component mounts with isDateConfirmationTooltipShown = true
  // Use ref to prevent recalculation on re-renders (which would hide tooltip after cookie is set)
  const hasValidationReason = Boolean(dateValidationReason)
  const cookieCheckedRef = useRef(false)
  const initialCookieShownRef = useRef<boolean | null>(null)

  if (
    !hasValidationReason &&
    !cookieCheckedRef.current &&
    isDateConfirmationTooltipShown
  ) {
    initialCookieShownRef.current = getDateConfirmationTooltipShown()
    cookieCheckedRef.current = true
  }

  const isHydrated = useIsHydrated()

  const showDateConfirmationTooltip =
    isHydrated &&
    isDateConfirmationTooltipShown &&
    (hasValidationReason || initialCookieShownRef.current === false)

  return (
    <FormContainer className="--search-form-horizontal-container">
      <SearchFormGrid
        container
        spacing="s000"
        wrap="nowrap"
        className="--search-form-horizontal-grid"
      >
        {showDestination && (
          <>
            <DestinationWrapper>
              <FormButton
                size="md"
                id="DestinationButton"
                dataId="DestinationButton"
                onClick={onDestinationPickerClick}
                icon={
                  <IconWrapper>
                    <Icon name="Pin" />
                  </IconWrapper>
                }
                isRounded
                placeholder={formatMessage(
                  destinationPickerMessages.destinationPlaceholder
                )}
                className={cx(
                  showDestination && '--is-first-input',
                  destinationHasError && '--has-error',
                  isDestinationPickerOpen && '--is-active'
                )}
              >
                {destinationButtonDisplayValue && (
                  <ButtonContent data-id="DestinationValue" dir="auto">
                    {destinationButtonDisplayValue}
                  </ButtonContent>
                )}
              </FormButton>
              {isDestinationPickerOpen && (
                <div ref={destinationInputRef}>
                  <SearchFormDestinationInput
                    hasError={destinationHasError}
                    focusOnMount
                    shouldLoadUserSearchHistory
                    destination={destinationInputDisplayValue}
                    onSuggestionsRequested={onSuggestionsRequested}
                  />
                </div>
              )}
            </DestinationWrapper>
            <Divider />
          </>
        )}
        <SearchDateTooltipWrapper
          isDateConfirmationTooltipShown={showDateConfirmationTooltip}
          dateValidationReason={dateValidationReason}
          trackEvent={trackEvent}
        >
          <DateWrapper>
            <FormButton
              size="md"
              id="CheckInInput"
              dataId="CheckInInput"
              onClick={onCheckInDatePickerClick}
              icon={
                <IconWrapper>
                  <Icon name="CalendarStart" />
                </IconWrapper>
              }
              isRounded
              placeholder={formatMessage(datePickerMessages.checkInDate)}
              className={cx(
                !showDestination && '--is-first-input',
                isCheckInPickerOpen && '--is-active',
                'chromatic-ignore'
              )}
            >
              {checkInDate && <ButtonContent>{checkInDate}</ButtonContent>}
            </FormButton>
          </DateWrapper>
          <Divider />
          <DateWrapper>
            <FormButton
              size="md"
              id="CheckOutInput"
              dataId="CheckOutInput"
              onClick={onCheckOutDatePickerClick}
              icon={
                <IconWrapper>
                  <Icon name="CalendarEnd" />
                </IconWrapper>
              }
              isRounded
              placeholder={formatMessage(datePickerMessages.checkOutDate)}
              className={cx(
                isCheckOutPickerOpen && '--is-active',
                'chromatic-ignore'
              )}
            >
              {checkOutDate && <ButtonContent>{checkOutDate}</ButtonContent>}
            </FormButton>
          </DateWrapper>
        </SearchDateTooltipWrapper>
        <Divider />
        <GuestWrapper ref={guestPickerRef}>
          <FormButton
            size="md"
            id="GuestInput"
            dataId="GuestInput"
            onClick={onGuestPickerClick}
            icon={
              <IconWrapper>
                <Icon name="People" />
              </IconWrapper>
            }
            isRounded
            className={cx(isGuestPickerOpen && '--is-active')}
          >
            <ButtonContent>
              {guestMessage}
              {isRoomsLabelShown && <>, {roomsMessage}</>}
            </ButtonContent>
          </FormButton>
        </GuestWrapper>

        <Grid mobileSm="fit">
          <SearchButtonWrapper>
            <Button
              variant="primary"
              size="lg"
              dataId="SearchButton"
              onClick={onSubmit}
              iconStart={<Icon name="Search" />}
              isRounded
            >
              <FormattedMessageWrapper id="search" defaultMessage="Search" />
            </Button>
          </SearchButtonWrapper>
        </Grid>
      </SearchFormGrid>
    </FormContainer>
  )
})
